// The could-help matching surface (bead hf-e5-availability-ccp.2, plan §11, §10.1).
//
// Pure components over `matchingData.ts`. Copy from `@herefor/product-language`,
// components from `@herefor/design-system`.
//
// ── THIS SURFACE SUGGESTS. IT DOES NOT ACT. ────────────────────────────────────
//
// Three words, and the whole design is keeping them apart:
//
//   COULD HELP  — a possibility somebody recorded about their own week.
//   ASKED       — a question an organiser chose to send to one named person.
//   AGREED      — a promise.
//
// They are not degrees of one thing. `AVAILABILITY_MESSAGES.meaning` promises the
// helper that recording availability "does not book you in"; this screen is where that
// promise is kept or broken, because it is the only place an organiser sees the list
// and could be tempted to treat it as a roster.
//
// So, concretely, and each of these is asserted in the tests:
//
//   - There is NO bulk action. No "ask everyone", no "ask the best match". Every ask
//     is one organiser choosing one named person (AVL-026, the bead's consent
//     framing). A select-all here would turn availability into a rota overnight.
//   - There is NO ranking language and no score. Matching is containment plus mode
//     plus absence of conflict — a yes-or-no property — so this is a list of equals.
//     The order favours fresher and self-recorded records, which is about trusting the
//     RECORD, not about rating the person.
//   - An ASKED candidate is marked, and the mark never reads as safer than it is. It
//     says a question is outstanding, in the same §10.1 words the need's own chip
//     uses, because "asked" is not a weaker kind of "covered".
//   - PROVENANCE travels with a proxy record (AVL-011, COM-009). An organiser
//     deciding whom to ask should know whether the person said this themselves.

import { Button, Card, EmptyState, StatusMessage } from "@herefor/design-system";
import { MATCHING_SURFACE_MESSAGES as COPY, STATUS_CONTEXT } from "@herefor/product-language";

import type { CandidateView } from "./matchingData";

export interface CouldHelpListProps {
  readonly candidates: readonly CandidateView[];
  /**
   * Server-decided: may this person send an ask? (AUT-007.)
   *
   * The ONLY gate on the action now that the Epic 6 stub flag is gone. A view-only
   * member is offered no control, because the server said they may not ask — never
   * because a constant in this app said the feature was not ready.
   */
  readonly canAsk: boolean;
  /**
   * What happens when an organiser chooses one name.
   *
   * OPTIONAL, and that is deliberate rather than lazy: this surface SUGGESTS and never
   * acts, so a placement that only wants the list — a read-only view, a test, a print
   * preview — omits it and gets no action at all. `NeedAskPanel` is what fills it, and
   * the confirmation step, the mutation and the refusal handling all live there.
   */
  readonly onAsk?: (candidate: CandidateView) => void;
}

/**
 * Who could help, and the sentence that stops it being a rota.
 *
 * The meaning line is not decoration. It is the organiser-facing half of the promise
 * the availability screen makes to the helper, and it sits ABOVE the list because a
 * caveat underneath a list of names is read after the names.
 */
export function CouldHelpList({ candidates, canAsk, onAsk }: CouldHelpListProps) {
  return (
    <Card title={COPY.title} titleLevel={2}>
      <p className="matching__meaning">{COPY.meaning}</p>

      {candidates.length === 0 ? (
        // Stated as a fact about the window, never as anybody's failing — and it says
        // what the organiser can still do, so the screen is not a dead end.
        <EmptyState description={COPY.noneBody} title={COPY.noneTitle} />
      ) : (
        <ul className="matching__list">
          {candidates.map((candidate) => (
            <li
              className="matching__candidate"
              data-asked={candidate.asked}
              key={candidate.personId}
            >
              <CandidateRow
                canAsk={canAsk}
                candidate={candidate}
                {...(onAsk === undefined ? {} : { onAsk })}
              />
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export interface CandidateRowProps {
  readonly candidate: CandidateView;
  readonly canAsk: boolean;
  readonly onAsk?: (candidate: CandidateView) => void;
}

export function CandidateRow({ candidate, canAsk, onAsk }: CandidateRowProps) {
  return (
    <>
      <p className="matching__name">{candidate.displayName}</p>
      <p className="matching__recorded">{COPY.recordedOn(candidate.recordedOnLabel)}</p>

      {candidate.source === "proxy" ? <Provenance candidate={candidate} /> : null}

      {candidate.asked ? (
        // REQ-003, §10.1: an outstanding ask is secondary context, in the SAME words
        // the need's own chip uses. It marks a question, not a softer kind of yes.
        <StatusMessage tone="info">
          <span className="matching__asked">
            {STATUS_CONTEXT.askedPersonWaiting(candidate.displayName)}
          </span>
        </StatusMessage>
      ) : null}

      {canAsk && onAsk !== undefined ? <AskAction candidate={candidate} onAsk={onAsk} /> : null}
    </>
  );
}

/**
 * The ask, one person at a time.
 *
 * Never rendered for more than one candidate at once BY CONSTRUCTION: this component
 * takes a single candidate, and there is no list-level action anywhere in this file.
 * That is what makes "no ask everyone, no ask the best match" a property of the code
 * rather than a promise in a comment — a bulk action would have to be written from
 * scratch, in a file whose tests assert it does not exist.
 *
 * It opens a confirmation rather than sending. `NeedAskPanel` owns what happens next;
 * this button's whole job is to report that an organiser chose one name.
 */
function AskAction({
  candidate,
  onAsk,
}: {
  readonly candidate: CandidateView;
  readonly onAsk: (candidate: CandidateView) => void;
}) {
  return (
    <Button
      onClick={() => {
        onAsk(candidate);
      }}
      variant="secondary"
    >
      {COPY.ask(candidate.displayName)}
    </Button>
  );
}

/**
 * Who wrote this availability down, when it was not the person themselves.
 *
 * AVL-011 and COM-009: a proxy record discloses its recorder wherever it is shown. An
 * organiser choosing whom to ask is exactly who needs to know that a note came from a
 * phone call somebody else made, not from the helper's own hands.
 */
function Provenance({ candidate }: { readonly candidate: CandidateView }) {
  if (candidate.recordedByName === null) return null;

  return (
    <p className="matching__provenance">
      {candidate.sourceChannel === null
        ? COPY.recordedBy(candidate.recordedByName)
        : COPY.recordedBySource(candidate.recordedByName, candidate.sourceChannel)}
    </p>
  );
}
